import { useEffect, useState } from "react";
import axios from "axios";
import { Box, Grid, Paper, Skeleton, Tooltip, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import ForumIcon from "@mui/icons-material/Forum";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TodayIcon from "@mui/icons-material/Today";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

import { hostname } from "../../utils";
import { DEMO_MODE_KEY } from "../../constants";
import {
  DEMO_AGENT_METRICS,
  generateDemoAgentsTimeseries,
} from "../../constants/demoData";

const CompactMetric = ({
  icon,
  title,
  value,
  tooltip,
}: {
  icon: React.ReactNode;
  title: string;
  value: string;
  tooltip: string;
}) => (
  <Tooltip title={tooltip} arrow placement="top">
    <Paper elevation={1} sx={{ px: 1.5, py: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
        <Box sx={{ color: "text.secondary", display: "flex", "& svg": { fontSize: 16 } }}>
          {icon}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ lineHeight: 1.1, fontWeight: 600 }}>
        {value}
      </Typography>
    </Paper>
  </Tooltip>
);

interface AgentMetrics {
  totalAgents: number;
  activeAgents: number;
  totalConversations: number;
  recentConversations: number;
  conversationsToday: number;
  recentMessages: number;
}

interface TimeseriesSeries {
  id?: string;
  name: string;
  label: string;
  data: number[];
}

interface TimeseriesResponse {
  dates: string[];
  series: TimeseriesSeries[];
}

interface Props {
  companyId: string;
  getToken: () => Promise<string>;
  showMetrics?: boolean;
}

const formatNumber = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const SEQUENCE_PALETTE = ["#1976d2", "#0288d1", "#00c853", "#ff9800"];

const shortenName = (name: string): string => {
  if (name.length <= 32) return name;
  return name.slice(0, 30).trimEnd() + "…";
};

const formatHourTick = (iso: string) => {
  const d = new Date(iso);
  if (d.getHours() === 0) {
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return "";
};

const formatHourTooltip = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
  });
};

const normalizeStats = (raw: any): AgentMetrics | null => {
  if (!raw) return null;
  return {
    totalAgents: raw.totalAgents ?? raw.total_agents ?? 0,
    activeAgents: raw.activeAgents ?? raw.active_agents ?? 0,
    totalConversations: raw.totalConversations ?? raw.total_conversations ?? 0,
    recentConversations: raw.recentConversations ?? raw.recent_conversations ?? 0,
    conversationsToday: raw.conversationsToday ?? raw.conversations_today ?? 0,
    recentMessages: raw.recentMessages ?? raw.recent_messages ?? 0,
  };
};

const AgentMetricsBar: React.FC<Props> = ({ companyId, getToken, showMetrics = true }) => {
  const [metrics, setMetrics] = useState<AgentMetrics | null>(null);
  const [timeseries, setTimeseries] = useState<TimeseriesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(
    () => localStorage.getItem(DEMO_MODE_KEY) === "true"
  );

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === DEMO_MODE_KEY) {
        setIsDemoMode(e.newValue === "true");
      }
    };
    const checkDemoMode = () => {
      const currentValue = localStorage.getItem(DEMO_MODE_KEY) === "true";
      setIsDemoMode((prev) => (prev !== currentValue ? currentValue : prev));
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(checkDemoMode, 500);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (isDemoMode) {
      setLoading(true);
      const timer = setTimeout(() => {
        if (!cancelled) {
          setMetrics(normalizeStats(DEMO_AGENT_METRICS));
          setTimeseries(generateDemoAgentsTimeseries());
          setLoading(false);
        }
      }, 300);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }

    const load = async () => {
      setLoading(true);
      try {
        const token = await getToken();
        const [statsRes, seriesRes] = await Promise.all([
          axios.get(`${hostname}/agents/statistics`, {
            params: { companyId },
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${hostname}/agents/timeseries`, {
            params: { companyId, days: 7 },
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!cancelled) {
          setMetrics(normalizeStats(statsRes.data));
          setTimeseries(seriesRes.data ?? null);
        }
      } catch (e) {
        if (!cancelled) {
          setMetrics(null);
          setTimeseries(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, getToken, isDemoMode]);

  if (loading) {
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        {showMetrics && (
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={2} key={i}>
                <Skeleton variant="rounded" height={56} />
              </Grid>
            ))}
          </Grid>
        )}
        <Skeleton variant="rounded" height={160} />
      </Box>
    );
  }

  if (!metrics || metrics.totalAgents === 0) {
    return null;
  }

  const chartSeries =
    timeseries?.series.map((s, i) => ({
      label: shortenName(s.label || s.name),
      data: s.data,
      color: SEQUENCE_PALETTE[i % SEQUENCE_PALETTE.length],
      stack: "activity",
      area: true,
      showMark: false,
      curve: "monotoneX" as const,
    })) ?? [];

  const chartDates = timeseries?.dates ?? [];
  const hasChartData = chartSeries.some((s) => s.data.some((v) => v > 0));

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      {showMetrics && (
        <Grid container spacing={1} sx={{ mb: 1 }}>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<SmartToyIcon />}
              title="Agents"
              value={formatNumber(metrics.totalAgents)}
              tooltip="Total agents"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<PowerSettingsNewIcon />}
              title="Active"
              value={formatNumber(metrics.activeAgents)}
              tooltip="Active agents"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<ForumIcon />}
              title="Conversations"
              value={formatNumber(metrics.totalConversations)}
              tooltip="Total conversations across all agents"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<TrendingUpIcon />}
              title="Last 7d"
              value={formatNumber(metrics.recentConversations)}
              tooltip="New conversations in the last 7 days"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<TodayIcon />}
              title="Today"
              value={formatNumber(metrics.conversationsToday)}
              tooltip="New conversations today"
            />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <CompactMetric
              icon={<ChatBubbleOutlineIcon />}
              title="Messages"
              value={formatNumber(metrics.recentMessages)}
              tooltip="Messages in the last 7 days"
            />
          </Grid>
        </Grid>
      )}
      <Paper elevation={1} sx={{ px: 1.5, py: 1 }}>
        {hasChartData ? (
          <Box sx={{ width: "100%", height: 160 }}>
            <LineChart
              xAxis={[
                {
                  scaleType: "point",
                  data: chartDates,
                  valueFormatter: (value: string, context: any) =>
                    context?.location === "tooltip"
                      ? formatHourTooltip(value)
                      : formatHourTick(value),
                  tickLabelInterval: (value: string) => {
                    const d = new Date(value);
                    return d.getHours() === 0;
                  },
                },
              ]}
              yAxis={[{ disableLine: true, disableTicks: true }]}
              series={chartSeries}
              height={160}
              margin={{ top: 10, right: 10, bottom: 24, left: 10 }}
              slotProps={{ legend: { hidden: true } }}
              sx={{
                "& .MuiAreaElement-root": { fillOpacity: 0.35 },
                "& .MuiChartsAxis-tickLabel": { fontSize: "0.7rem" },
                "& .MuiChartsAxis-left .MuiChartsAxis-tickLabel": { display: "none" },
              }}
            />
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", py: 1, textAlign: "center" }}>
            No activity in the last 7 days yet.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default AgentMetricsBar;
